import {
  ShieldAlert,
  Target,
  Waypoints,
  ListChecks,
  Ticket,
  CalendarClock,
  Clock,
  Check,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { Badge, Button, Card, CardBody } from '../../ui';
import {
  ageLabel,
  ageTone,
  formatDate,
  scopeLabel,
  scopeTone,
} from '../../features/blockers/blockerModel';
import type { BlockerSummary } from '../../features/blockers/types';

export interface BlockerCardProps {
  blocker: BlockerSummary;
  /** Resolved affected-goal title, if any. */
  goalTitle?: string;
  /** Resolved blocked-workstream title, if any. */
  workstreamTitle?: string;
  /** Resolved blocked-story title, if any. */
  storyTitle?: string;
  onOpenGoal?: (goalId: string) => void;
  onOpenWorkstream?: (workstreamId: string) => void;
  onResolve?: (blocker: BlockerSummary) => void;
  onReopen?: (blocker: BlockerSummary) => void;
  onDelete?: (blocker: BlockerSummary) => void;
}

/** A small labeled field for the blocker's who/when meta grid. */
function Field({ label, value }: { label: string; value: string }) {
  if (!value.trim()) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
        {label}
      </p>
      <p className="mt-0.5 text-sm text-zinc-200">{value}</p>
    </div>
  );
}

/**
 * A single Blockers-board card. Leads with the "BLOCKED — N DAYS" age badge and
 * the affected goal / delivery impact, then the who/when detail and links. Open
 * blockers can be resolved; resolved ones can be reopened.
 */
export function BlockerCard({
  blocker,
  goalTitle,
  workstreamTitle,
  storyTitle,
  onOpenGoal,
  onOpenWorkstream,
  onResolve,
  onReopen,
  onDelete,
}: BlockerCardProps) {
  const resolved = blocker.status === 'RESOLVED';

  return (
    <Card className={resolved ? 'opacity-70' : undefined}>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={ageTone(blocker)} dot>
              {ageLabel(blocker)}
            </Badge>
            <Badge tone={scopeTone(blocker.internalExternal)}>
              {scopeLabel(blocker.internalExternal)}
            </Badge>
            {blocker.forecastImpactDays !== undefined &&
              blocker.forecastImpactDays > 0 && (
                <Badge tone="warning">
                  <Clock className="h-3 w-3" /> ~{blocker.forecastImpactDays}d impact
                </Badge>
              )}
          </div>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(blocker)}
              aria-label="Delete blocker"
              className="shrink-0 rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="flex items-start gap-2">
          <ShieldAlert
            className="mt-0.5 h-4 w-4 shrink-0 text-red-300"
            aria-hidden="true"
          />
          <p className="whitespace-pre-wrap text-sm font-medium text-zinc-100">
            {blocker.summary || 'Untitled blocker'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Blocked" value={blocker.blockedEngineer} />
          <Field label="Dependency owner" value={blocker.dependencyOwner} />
          <Field label="Blocking since" value={formatDate(blocker.blockingSince)} />
          <Field
            label="Expected"
            value={formatDate(blocker.expectedResolution) || '—'}
          />
          {blocker.escalationOwner && (
            <Field label="Escalation" value={blocker.escalationOwner} />
          )}
        </div>

        {/* Links + related ticket. */}
        <div className="flex flex-wrap items-center gap-1.5">
          {blocker.affectedGoalId && (
            <button
              type="button"
              onClick={() => blocker.affectedGoalId && onOpenGoal?.(blocker.affectedGoalId)}
              disabled={!onOpenGoal}
            >
              <Badge tone="accent">
                <Target className="h-3 w-3" /> {goalTitle ?? 'Goal'}
              </Badge>
            </button>
          )}
          {blocker.blockedWorkstreamId && (
            <button
              type="button"
              onClick={() =>
                blocker.blockedWorkstreamId &&
                onOpenWorkstream?.(blocker.blockedWorkstreamId)
              }
              disabled={!onOpenWorkstream}
            >
              <Badge tone="info">
                <Waypoints className="h-3 w-3" /> {workstreamTitle ?? 'Workstream'}
              </Badge>
            </button>
          )}
          {blocker.blockedStoryId && (
            <Badge tone="neutral">
              <ListChecks className="h-3 w-3" /> {storyTitle ?? 'Story'}
            </Badge>
          )}
          {blocker.relatedTicket && (
            <Badge tone="neutral">
              <Ticket className="h-3 w-3" /> {blocker.relatedTicket}
            </Badge>
          )}
        </div>

        {/* Actions. */}
        <div className="flex items-center gap-2 pt-1">
          {resolved ? (
            <>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                <Check className="h-3.5 w-3.5" aria-hidden="true" /> Resolved
              </span>
              {onReopen && (
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                  onClick={() => onReopen(blocker)}
                >
                  Reopen
                </Button>
              )}
            </>
          ) : (
            onResolve && (
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Check className="h-3.5 w-3.5" />}
                onClick={() => onResolve(blocker)}
              >
                Mark resolved
              </Button>
            )
          )}
          {!resolved && blocker.expectedResolution && (
            <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
              <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
              expected {formatDate(blocker.expectedResolution)}
            </span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default BlockerCard;
